import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import {
  Menu,
  X,
  ArrowUp,
  Home,
  Sparkles,
  User,
  LogOut,
  Plus,
  PanelLeftClose,
  PanelLeftOpen,
  MessageSquareText,
  Trash2,
  Check,
} from "lucide-react";
import { apiFetch, getToken } from "@/api";
import Paywall from "@/components/Paywall";

const SUGGESTIONS = [
  "Explain a concept",
  "Solve a question",
  "Teach me step by step",
  "Give me examples",
  "Help me revise",
  "Test my knowledge",
];

/* Abhyas AI ambient aura — soft blurred patches drifting left → right,
   confined to the top/bottom edges plus faint far-left/right glows.
   Always decorative: z-0, pointer-events-none, central area stays clean. */
const AURA_TOP = [
  { c: "22,163,74", o1: 0.16, o2: 0.34, w: 420, h: 170, top: 24, dur: "40s", del: "-4s" },
  { c: "163,230,53", o1: 0.13, o2: 0.28, w: 330, h: 140, top: 80, dur: "52s", del: "-22s" },
  { c: "34,211,238", o1: 0.12, o2: 0.26, w: 360, h: 150, top: 18, dur: "46s", del: "-35s" },
  { c: "167,139,250", o1: 0.1, o2: 0.22, w: 300, h: 130, top: 96, dur: "58s", del: "-14s" },
];
const AURA_BOTTOM = [
  { c: "250,204,21", o1: 0.14, o2: 0.28, w: 350, h: 150, top: 64, dur: "48s", del: "-9s" },
  { c: "22,163,74", o1: 0.14, o2: 0.3, w: 430, h: 170, top: 96, dur: "42s", del: "-30s" },
  { c: "96,165,250", o1: 0.12, o2: 0.24, w: 340, h: 145, top: 44, dur: "56s", del: "-19s" },
  { c: "163,230,53", o1: 0.11, o2: 0.23, w: 310, h: 135, top: 118, dur: "50s", del: "-42s" },
];
/* faint glows hugging the far left/right edges — gentle horizontal drift only */
const AURA_EDGES = [
  { c: "34,211,238", o1: 0.1, o2: 0.2, w: 240, h: 320, side: "left", top: "22%", dur: "24s", del: "-6s" },
  { c: "167,139,250", o1: 0.09, o2: 0.18, w: 220, h: 300, side: "left", top: "62%", dur: "30s", del: "-18s" },
  { c: "250,204,21", o1: 0.1, o2: 0.2, w: 230, h: 310, side: "right", top: "18%", dur: "27s", del: "-11s" },
  { c: "22,163,74", o1: 0.11, o2: 0.22, w: 250, h: 330, side: "right", top: "58%", dur: "22s", del: "-25s" },
];

const AuraBand = ({ pos, patches }) => (
  <div className={`aura-band ${pos}`}>
    {patches.map((p, i) => (
      <div
        key={i}
        className="aura-patch"
        style={{
          width: p.w,
          height: p.h,
          top: p.top,
          left: 0,
          background: `radial-gradient(ellipse, rgba(${p.c},0.85) 0%, rgba(${p.c},0) 70%)`,
          "--o1": p.o1,
          "--o2": p.o2,
          "--dur": p.dur,
          animationDelay: `${p.del}, ${p.del}`,
        }}
      />
    ))}
  </div>
);

const Aurora = () => (
  <div
    data-testid="aurora"
    className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
  >
    <AuraBand pos="top" patches={AURA_TOP} />
    <AuraBand pos="bottom" patches={AURA_BOTTOM} />
    {AURA_EDGES.map((p, i) => (
      <div
        key={i}
        className="aura-edge"
        style={{
          width: p.w,
          height: p.h,
          top: p.top,
          [p.side]: -p.w / 2.4,
          background: `radial-gradient(ellipse, rgba(${p.c},0.8) 0%, rgba(${p.c},0) 70%)`,
          "--o1": p.o1,
          "--o2": p.o2,
          "--dur": p.dur,
          animationDelay: `${p.del}, ${p.del}`,
        }}
      />
    ))}
  </div>
);

const mdComponents = {
  h1: (p) => <h2 className="mt-6 mb-2 font-display text-xl font-extrabold text-[#17201A]" {...p} />,
  h2: (p) => <h3 className="mt-6 mb-2 font-display text-lg font-extrabold text-[#17201A]" {...p} />,
  h3: (p) => <h4 className="mt-5 mb-1.5 font-display text-base font-extrabold text-[#17201A]" {...p} />,
  p: (p) => <p className="my-2.5 text-[0.95rem] leading-relaxed text-[#17201A]" {...p} />,
  ul: (p) => <ul className="my-2.5 list-disc space-y-1.5 pl-6 text-[0.95rem] leading-relaxed text-[#17201A]" {...p} />,
  ol: (p) => <ol className="my-2.5 list-decimal space-y-1.5 pl-6 text-[0.95rem] leading-relaxed text-[#17201A]" {...p} />,
  li: (p) => <li className="pl-1" {...p} />,
  strong: (p) => <strong className="font-extrabold text-[#17201A]" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  a: (p) => <a className="font-bold text-[#16A34A] underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  code: ({ inline, children, ...p }) =>
    inline ? (
      <code className="rounded-md bg-[#F0F4F1] px-1.5 py-0.5 font-mono text-[0.85em] text-[#14532D]" {...p}>{children}</code>
    ) : (
      <code className="font-mono text-[0.85em]" {...p}>{children}</code>
    ),
  pre: (p) => (
    <pre className="my-3 overflow-x-auto rounded-2xl border border-[#E3ECE6] bg-[#17201A] p-4 text-[#F8FAF7]" {...p} />
  ),
  blockquote: (p) => (
    <blockquote className="my-3 border-l-2 border-[#FACC15] pl-4 text-[#647067]" {...p} />
  ),
};

const PromptLearning = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // {title, body, upgrade}
  const [showPaywall, setShowPaywall] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // desktop: expanded by default
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile/tablet overlay
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [streamText, setStreamText] = useState(null); // non-null while an answer is revealing
  const [smart, setSmart] = useState(null); // {suggestions, contextLine} — student-context chips
  const streamTimer = useRef(null);
  const taRef = useRef(null);
  const bottomRef = useRef(null);
  const started = messages.length > 0;

  useEffect(() => {
    if (!getToken()) navigate("/login");
  }, [navigate]);

  const loadHistory = useCallback(() => {
    if (!getToken()) return;
    apiFetch("/prompt-ai/conversations")
      .then((r) => setHistory(r.conversations || []))
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Abhyas 2.0: contextual suggestions from the student's weak areas / plan
  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/prompt-ai/suggestions")
      .then(setSmart)
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: streamText !== null ? "auto" : "smooth",
      block: "end",
    });
  }, [messages, busy, notice, streamText]);

  // stop the reveal timer if the page is left mid-stream
  useEffect(
    () => () => {
      if (streamTimer.current) clearInterval(streamTimer.current);
    },
    []
  );

  // Progressive word-by-word reveal — display only; the backend has already
  // returned the full answer and settled the credits by this point.
  const streamAnswer = (full) => {
    const tokens = full.split(/(\s+)/); // whitespace preserved so markdown stays intact
    const step = Math.max(6, Math.ceil(tokens.length / 90)); // long answers reveal faster
    let i = 0;
    setStreamText("");
    streamTimer.current = setInterval(() => {
      i += step;
      if (i >= tokens.length) {
        clearInterval(streamTimer.current);
        streamTimer.current = null;
        setStreamText(null);
        setMessages((m) => [...m, { role: "assistant", content: full }]);
        setBusy(false);
        return;
      }
      setStreamText(tokens.slice(0, i).join(""));
    }, 40);
  };

  const autosize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 220) + "px";
  };

  const send = async (text) => {
    const value = (text ?? prompt).trim();
    if (!value || busy) return;
    setNotice(null);
    setBusy(true);
    const isNewConversation = !conversationId;
    setMessages((m) => [...m, { role: "user", content: value }]);
    setPrompt("");
    requestAnimationFrame(autosize);
    const requestId = crypto.randomUUID();
    try {
      const res = await apiFetch("/prompt-ai/chat", {
        method: "POST",
        body: { prompt: value, conversation_id: conversationId, request_id: requestId },
      });
      setConversationId(res.conversation_id);
      if (isNewConversation) loadHistory(); // first message persisted it — refresh titles
      streamAnswer(res.message.content); // busy clears when the reveal finishes
    } catch (err) {
      let payload = null;
      try {
        payload = JSON.parse(err.message);
      } catch {
        payload = null;
      }
      if (payload?.status === "insufficient_credits") {
        setNotice({ kind: "limit", title: payload.message, upgrade: true });
      } else if (payload?.status === "rate_limited") {
        setNotice({ kind: "info", title: payload.message });
      } else {
        setNotice({
          kind: "error",
          title: payload?.message || "Abhyas AI couldn't answer right now. No credits were used — please try again.",
        });
      }
      setBusy(false);
    }
  };

  const newChat = () => {
    // No backend call — a conversation is only persisted once the student
    // actually sends a message, so clicking New Chat never creates empty rows.
    if (busy) return;
    setMessages([]);
    setConversationId(null);
    setNotice(null);
    setConfirmDeleteId(null);
    setDrawerOpen(false);
    requestAnimationFrame(() => {
      autosize();
      taRef.current?.focus();
    });
  };

  const openConversation = async (id) => {
    if (busy || openingId || id === conversationId) {
      if (id === conversationId) setDrawerOpen(false);
      return;
    }
    setOpeningId(id);
    setConfirmDeleteId(null);
    try {
      const res = await apiFetch(`/prompt-ai/conversations/${id}/messages`);
      setMessages(res.messages.map((m) => ({ role: m.role, content: m.content })));
      setConversationId(id);
      setNotice(null);
      setDrawerOpen(false);
    } catch {
      // conversation may have expired per the plan's retention — refresh list
      loadHistory();
      setNotice({ kind: "info", title: "That conversation is no longer available." });
    } finally {
      setOpeningId(null);
    }
  };

  const deleteConversation = async (id) => {
    try {
      await apiFetch(`/prompt-ai/conversations/${id}`, { method: "DELETE" });
      setHistory((h) => h.filter((c) => c.id !== id));
      if (id === conversationId) {
        setMessages([]);
        setConversationId(null);
      }
    } catch {
      /* keep the item if the delete failed */
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const logout = () => {
    apiFetch("/auth/logout", { method: "POST" }).catch(() => {});
    localStorage.removeItem("abhyas_token");
    localStorage.removeItem("abhyas_profile");
    navigate("/");
  };

  const composer = (
    <div
      data-testid="composer"
      className="rounded-[2rem] border border-[#E3ECE6] bg-white p-3 pl-6 shadow-[0_20px_50px_-20px_rgba(22,32,26,0.25)] transition-[border-color,box-shadow] duration-200 focus-within:border-[#16A34A]/50"
    >
      <div className="flex items-end gap-3">
        <textarea
          ref={taRef}
          data-testid="composer-input"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            autosize();
          }}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Ask Abhyas anything about your studies…"
          className="max-h-[220px] flex-1 resize-none bg-transparent py-2.5 text-[0.95rem] font-medium leading-relaxed text-[#17201A] outline-none placeholder:text-[#94A39B]"
        />
        <button
          data-testid="composer-send"
          onClick={() => send()}
          disabled={!prompt.trim() || busy}
          aria-label="Send"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#16A34A] text-white transition-[background-color,transform,opacity] duration-200 hover:bg-[#15803D] active:scale-95 disabled:opacity-35"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  /* Shared sidebar body — desktop column + mobile drawer */
  const sidebarBody = (isDrawer) => {
    const P = isDrawer ? "m-" : ""; // unique testids per sidebar instance
    return (
    <>
      <div className="flex items-center justify-between px-5 pb-2 pt-5">
        <img
          src="/abhyas-wordmark.png"
          alt="Abhyas"
          data-testid={isDrawer ? "ai-wordmark-mobile" : "ai-wordmark-sidebar"}
          className="h-7 w-auto"
        />
        {isDrawer && (
          <button
            data-testid="ai-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#647067] transition-colors hover:bg-[#F0F4F1]"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="px-4 pt-2">
        <button
          data-testid={isDrawer ? "ai-new-chat-mobile" : "ai-new-chat"}
          onClick={newChat}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-[#16A34A]/40 bg-white px-4 py-2.5 text-sm font-bold text-[#16A34A] transition-[background-color,color,border-color] duration-200 hover:border-[#16A34A] hover:bg-[#16A34A] hover:text-white"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </button>
      </div>

      <p className="px-6 pb-2 pt-6 text-[10px] font-bold uppercase tracking-[0.15em] text-[#94A39B]">
        Recent chats
      </p>
      <div
        data-testid={isDrawer ? "ai-history-list-mobile" : "ai-history-list"}
        className="flex-1 overflow-y-auto px-3 pb-3"
      >
        {historyLoaded && history.length === 0 && (
          <p className="px-3 py-4 text-xs font-semibold leading-relaxed text-[#94A39B]">
            No conversations yet — ask your first question and it will appear here.
          </p>
        )}
        {!historyLoaded && (
          <p className="px-3 py-4 text-xs font-semibold text-[#94A39B]">Loading chats…</p>
        )}
        {history.map((c) => (
          <div key={c.id} className="group relative mb-1">
            {confirmDeleteId === c.id ? (
              <div
                data-testid={`ai-delete-confirm-${P}${c.id}`}
                className="flex items-center justify-between gap-2 rounded-xl bg-[#FEF2F2] px-3 py-2.5"
              >
                <span className="text-xs font-bold text-[#B91C1C]">Delete forever?</span>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`ai-delete-yes-${P}${c.id}`}
                    onClick={() => deleteConversation(c.id)}
                    aria-label="Confirm delete"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-[#DC2626] text-white transition-colors hover:bg-[#B91C1C]"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`ai-delete-no-${P}${c.id}`}
                    onClick={() => setConfirmDeleteId(null)}
                    aria-label="Cancel delete"
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E3ECE6] bg-white text-[#647067] transition-colors hover:text-[#17201A]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`flex items-center gap-1 rounded-xl pr-1 transition-colors duration-150 ${
                  c.id === conversationId ? "bg-[#16A34A]/10" : "hover:bg-[#F0F4F1]"
                }`}
              >
                <button
                  data-testid={`ai-history-item-${P}${c.id}`}
                  onClick={() => openConversation(c.id)}
                  className={`flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-left text-[13px] font-semibold ${
                    c.id === conversationId ? "text-[#16A34A]" : "text-[#17201A]"
                  }`}
                >
                  <MessageSquareText
                    className={`h-4 w-4 shrink-0 ${
                      c.id === conversationId ? "text-[#16A34A]" : "text-[#94A39B]"
                    }`}
                  />
                  <span className="truncate">
                    {openingId === c.id ? "Opening…" : c.title}
                  </span>
                </button>
                <button
                  data-testid={`ai-history-delete-${P}${c.id}`}
                  onClick={() => setConfirmDeleteId(c.id)}
                  aria-label="Delete conversation"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#94A39B] opacity-60 transition-[opacity,color,background-color] duration-150 hover:bg-white hover:text-[#DC2626] group-hover:opacity-100 sm:opacity-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-[#E3ECE6] p-3">
        {[
          { label: "Profile", to: "/dashboard", icon: User, testid: "ai-nav-profile" },
          { label: "Home", to: "/", icon: Home, testid: "ai-nav-home" },
        ].map((item) => (
          <button
            key={item.label}
            data-testid={isDrawer ? `${item.testid}-mobile` : item.testid}
            onClick={() => {
              setDrawerOpen(false);
              navigate(item.to);
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] font-bold text-[#17201A] transition-colors duration-150 hover:bg-[#F0F4F1]"
          >
            <item.icon className="h-4 w-4 text-[#647067]" />
            {item.label}
          </button>
        ))}
        <button
          data-testid={isDrawer ? "ai-nav-sign-out-mobile" : "ai-nav-sign-out"}
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] font-bold text-[#647067] transition-colors duration-150 hover:bg-[#F0F4F1] hover:text-[#DC2626]"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </>
    );
  };

  return (
    <div
      data-testid="abhyas-ai-page"
      className="relative flex h-screen overflow-hidden bg-[#F8FAF7]"
    >
      <Aurora />

      {/* Desktop sidebar — expanded by default, collapsible */}
      <aside
        data-testid="ai-sidebar"
        className={`relative z-20 hidden flex-col border-r border-[#E3ECE6] bg-white transition-[width,opacity] duration-300 ease-in-out lg:flex ${
          sidebarOpen ? "w-72 opacity-100" : "w-0 overflow-hidden border-r-0 opacity-0"
        }`}
      >
        <div className="flex h-full w-72 shrink-0 flex-col">{sidebarBody(false)}</div>
      </aside>

      {/* Mobile/tablet drawer overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            data-testid="ai-drawer-backdrop"
            className="absolute inset-0 bg-[#17201A]/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            data-testid="ai-drawer"
            className="drawer-in absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-2xl"
          >
            {sidebarBody(true)}
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header className="flex items-center gap-3 px-5 py-4 sm:px-8">
          <button
            data-testid="ai-menu-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3ECE6] bg-white text-[#17201A] transition-colors duration-200 hover:border-[#16A34A] lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <button
            data-testid="ai-sidebar-toggle"
            onClick={() => setSidebarOpen((s) => !s)}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            className="hidden h-10 w-10 items-center justify-center rounded-full border border-[#E3ECE6] bg-white text-[#17201A] transition-colors duration-200 hover:border-[#16A34A] lg:flex"
          >
            {sidebarOpen ? (
              <PanelLeftClose className="h-5 w-5" />
            ) : (
              <PanelLeftOpen className="h-5 w-5" />
            )}
          </button>
          <img
            src="/abhyas-wordmark.png"
            alt="Abhyas"
            data-testid="ai-wordmark"
            className="h-8 w-auto shrink-0"
          />
        </header>

        {/* Chat area */}
        {!started ? (
          <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center overflow-y-auto px-5 pb-16 pt-6 sm:px-8">
            <div className="text-center">
              <h2
                data-testid="ai-hero-title"
                className="animate-fade-up font-display text-4xl font-extrabold tracking-tight text-[#17201A] sm:text-5xl"
              >
                Learn anything. <span className="text-[#16A34A]">Ask Abhyas.</span>
              </h2>
              <p className="animate-fade-up mx-auto mt-4 max-w-md text-base leading-relaxed text-[#647067]" style={{ animationDelay: "0.1s" }}>
                {smart?.contextLine
                  ? `${smart.contextLine} What are you stuck on?`
                  : "Ask questions, understand concepts, solve problems and learn at your own pace."}
              </p>
            </div>
            <div className="animate-fade-up mt-10" style={{ animationDelay: "0.2s" }}>
              {composer}
            </div>
            <div
              data-testid="suggestions"
              className="animate-fade-up mt-6 flex flex-wrap items-center justify-center gap-2"
              style={{ animationDelay: "0.3s" }}
            >
              {(smart?.personalized && smart?.suggestions?.length ? smart.suggestions : SUGGESTIONS).map((s) => {
                const isSmart = Boolean(smart?.personalized && smart?.suggestions?.length);
                return (
                  <button
                    key={s}
                    data-testid={`suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}
                    onClick={() => {
                      // contextual chips are complete questions; static ones are prefixes
                      setPrompt(isSmart ? s : s + ": ");
                      taRef.current?.focus();
                      requestAnimationFrame(autosize);
                    }}
                    className={`rounded-full border px-4 py-2 text-xs font-bold backdrop-blur transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#16A34A] hover:text-[#16A34A] ${
                      isSmart
                        ? "border-[#16A34A]/25 bg-[#16A34A]/8 text-[#14532D]"
                        : "border-[#E3ECE6] bg-white/70 text-[#647067]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </main>
        ) : (
          <>
            <main className="flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-3xl px-5 pb-6 pt-4 sm:px-8">
                <div data-testid="conversation" className="space-y-6">
                  {messages.map((m, i) =>
                    m.role === "user" ? (
                      <div key={i} data-testid={`msg-user-${i}`} className="flex justify-end pl-10 sm:pl-24">
                        <p className="max-w-full whitespace-pre-wrap break-words rounded-[1.4rem] rounded-br-md bg-[#DCFCE7] px-4 py-2.5 text-[0.95rem] font-semibold leading-relaxed text-[#14532D] sm:px-5 sm:py-3">
                          {m.content}
                        </p>
                      </div>
                    ) : (
                      <div key={i} data-testid={`msg-ai-${i}`} className="pr-2 sm:max-w-[88%] sm:pr-0">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                          components={mdComponents}
                        >
                          {m.content}
                        </ReactMarkdown>
                      </div>
                    )
                  )}
                  {streamText !== null && (
                    <div data-testid="msg-ai-streaming" className="pr-2 sm:max-w-[88%] sm:pr-0">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                        components={mdComponents}
                      >
                        {streamText}
                      </ReactMarkdown>
                    </div>
                  )}
                  {busy && streamText === null && (
                    <div
                      data-testid="ai-thinking"
                      aria-label="Abhyas is thinking"
                      className="flex items-center gap-1.5 py-2"
                    >
                      <span className="thinking-dot" />
                      <span className="thinking-dot" style={{ animationDelay: "0.15s" }} />
                      <span className="thinking-dot" style={{ animationDelay: "0.3s" }} />
                    </div>
                  )}
                  {notice && (
                    <div
                      data-testid="ai-notice"
                      className="rounded-[1.5rem] border border-[#E3ECE6] bg-white p-6 text-center shadow-sm"
                    >
                      <p className="text-sm font-bold text-[#17201A]">{notice.title}</p>
                      {notice.upgrade && (
                        <div className="mt-4 flex justify-center gap-3">
                          <button
                            data-testid="notice-go-back"
                            onClick={() => setNotice(null)}
                            className="rounded-full border border-[#E3ECE6] px-5 py-2.5 text-sm font-bold text-[#647067] transition-colors hover:text-[#17201A]"
                          >
                            Go Back
                          </button>
                          <button
                            data-testid="notice-upgrade"
                            onClick={() => setShowPaywall(true)}
                            className="rounded-full bg-[#16A34A] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#15803D]"
                          >
                            Upgrade Plan
                          </button>
                        </div>
                      )}
                      {!notice.upgrade && (
                        <button
                          data-testid="notice-dismiss"
                          onClick={() => setNotice(null)}
                          className="mt-3 text-xs font-bold text-[#16A34A] hover:underline"
                        >
                          Okay
                        </button>
                      )}
                    </div>
                  )}
                  <div ref={bottomRef} />
                </div>
              </div>
            </main>
            <div className="mx-auto w-full max-w-3xl px-5 pb-5 pt-2 sm:px-8">
              {composer}
            </div>
          </>
        )}
      </div>

      <Paywall open={showPaywall} onClose={() => setShowPaywall(false)} />
    </div>
  );
};

export default PromptLearning;
